import {
  addWeeks,
  parse,
  setHours,
  setMinutes,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'
import subDays from 'date-fns/subDays'
import { get } from 'lodash'

import {
  FRONTEND_DATE_FORMAT_DATE_FNS,
  BACKEND_DATE_FORMAT_DATE_FNS,
  FRONTEND_NOTIFICATION_DATE_FORMAT,
  FRONTEND_TIME_FORMAT_DATE_FNS,
  FRONTEND_DATE_WITH_WEEKDAY_FORMAT_DATE_FNS,
  URL_DATE_FORMAT_DATE_FNS,
  MOVE_BETWEEN_ROUTES_DATE_FORMAT_DATE_FNS,
  SHORT_FRONTEND_DATE_FORMAT_DATE_FNS,
  FRONTEND_FULL_MONTH_DATE_FORMAT,
} from 'settings/constants/date'

export const isNotInvalidDate = date => date instanceof Date && !Number.isNaN(date.getTime())

// Format should be YYYY-MM-DD, output date
export const createDateFromString = date => {
  if (!date) {
    return null
  }

  // This gives one day less in negative UTC country, example central america timezone
  // return  new Date(date);

  const dateSplit = date?.split('-')
  // Month start from 0, so -1
  return new Date(get(dateSplit, '[0]', undefined), get(dateSplit, '[1]', 1) - 1, get(dateSplit, '[2]', undefined))
}

// Replicates how moment() interpreted its input: undefined → now, Date → as-is,
// epoch number → Date, date-only ISO string → local midnight, other strings → Date parsing,
// moment-like objects → their .toDate(), anything else → Invalid Date.
const toDate = value => {
  if (value === undefined) {
    return new Date()
  }
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'number') {
    return new Date(value)
  }
  if (typeof value === 'string') {
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? createDateFromString(value) : new Date(value)
  }
  if (value && typeof value.toDate === 'function') {
    return value.toDate()
  }
  return new Date(NaN)
}

// Mirrors moment's behavior of rendering the literal string `Invalid date` instead of throwing
const formatDateSafe = (date, dateFormat) => (isNotInvalidDate(date) ? format(date, dateFormat) : 'Invalid date')

export const isValidDate = date => {
  if (!date) {
    return false
  }

  return isNotInvalidDate(toDate(date))
}

export const calculateDaysDiff = date => Math.max(0, differenceInDays(new Date(), toDate(date)))

export const parseURLDateString = dateString => {
  try {
    return parse(dateString, URL_DATE_FORMAT_DATE_FNS, new Date())
  } catch (error) {
    console.error('Error parsing date:', error)
    return null
  }
}

export const calculateDaysDiffFromStringDates = (dateLeft, dateRight) => {
  if (!dateLeft || !dateRight) {
    return false
  }

  return differenceInDays(createDateFromString(dateLeft), createDateFromString(dateRight))
}

// Format date to frontend format
export const formatDateToFEFormatDateFns = date => (date ? format(date, FRONTEND_DATE_FORMAT_DATE_FNS) : date)
export const formatDateToBulkMoveStr = date => (date ? format(date, MOVE_BETWEEN_ROUTES_DATE_FORMAT_DATE_FNS) : date)
export const formatDateToURLFormatDateFns = date => (date ? format(date, URL_DATE_FORMAT_DATE_FNS) : date)
export const formatDateToFEwithWeekday = date => (date ? format(date, FRONTEND_DATE_WITH_WEEKDAY_FORMAT_DATE_FNS) : date)

export const formatDateToFETimeFormatDateFns = date => (date ? format(date, FRONTEND_TIME_FORMAT_DATE_FNS) : date)

// Format date to backend format
export const formatDateToBEFormatDateFns = date => (date ? format(date, BACKEND_DATE_FORMAT_DATE_FNS) : date)

export const formatDateToNotificationStr = date => (isNotInvalidDate(date) ? format(date, FRONTEND_NOTIFICATION_DATE_FORMAT) : date)

export const formatDateToFullMonthFormat = date => (isNotInvalidDate(date) ? format(date, FRONTEND_FULL_MONTH_DATE_FORMAT) : date)

/**
 * Helper function to format date strings to frontend format with validation
 * @param {string} dateString - The date string to format
 * @returns {string} - The formatted date string or empty string if invalid
 */
export const formatDateStringToFE = dateString => {
  if (!dateString || typeof dateString !== 'string') {
    return ''
  }

  const date = createDateFromString(dateString)
  if (!date || !isValidDate(date)) {
    return ''
  }

  return formatDateToFEFormatDateFns(date)
}

// Format date to frontend format
export const formatDateToFEFormat = date => (date ? formatDateSafe(toDate(date), FRONTEND_DATE_FORMAT_DATE_FNS) : date)

// Format date to backend format
export const formatDateToBEFormat = date => (date ? formatDateSafe(toDate(date), BACKEND_DATE_FORMAT_DATE_FNS) : date)

export const getLocalDate = date => {
  // Timestamps without an explicit offset are interpreted as UTC (moment.utc(date).local() semantics);
  // date-only ISO strings already parse as UTC midnight per spec
  const utcDate =
    typeof date === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(date) && !/([+-]\d{2}:\d{2}|Z)$/.test(date)
      ? new Date(`${date}Z`)
      : toDate(date)
  return formatDateSafe(utcDate, 'MM/dd/yyyy hh:mm a')
}

export const formatDate = (date = new Date(), dateFormat = FRONTEND_DATE_FORMAT_DATE_FNS) => formatDateSafe(toDate(date), dateFormat)

export const getUnixTimestamp = (date = new Date()) => Math.floor(toDate(date).getTime() / 1000)

export const getDateFromUnixTimestamp = timestamp => new Date(timestamp * 1000)

export const normalizeDateToApiDateFormat = date => formatDateSafe(toDate(date), BACKEND_DATE_FORMAT_DATE_FNS)

export const getCurrentDate = () => new Date()

export const getDateObjectFromDateString = (dateString = getCurrentDate()) => new Date(toDate(dateString).getTime())

// checks if the Date instance has a valid date
export const serializeDate = date => {
  return date?.toISOString()
}

export const deserializeDate = dateString => {
  return new Date(dateString)
}

export const getDateRangeForYesterday = date => {
  const yesterday = subDays(date, 1)
  return [startOfDay(yesterday), endOfDay(yesterday)]
}

export const getDateRangeForThisWeek = date => {
  const startDate = startOfWeek(date)
  const endDate = endOfWeek(date)
  return [startDate, endDate]
}

export const getDateRangeForLastWeek = date => {
  const startDate = startOfWeek(subWeeks(date, 1))
  const endDate = endOfWeek(subWeeks(date, 1))
  return [startDate, endDate]
}

export const getDateRangeForPastTwoWeeks = date => {
  const startDate = startOfWeek(subWeeks(date, 2))
  const endDate = endOfWeek(addWeeks(startDate, 1))
  return [startDate, endDate]
}

export const getDateRangeForThisMonth = date => {
  const startDate = startOfMonth(date)
  const endDate = endOfMonth(date)
  return [startDate, endDate]
}

export const getDateRangeForLastMonth = date => {
  const startDate = startOfMonth(subMonths(date, 1))
  const endDate = endOfMonth(startDate)
  return [startDate, endDate]
}

export const formatDateRangeToShortFeformat = dates =>
  dates
    .map(date => date && createDateFromString(date))
    .filter(date => date && isNotInvalidDate(date))
    .map(date => format(date, SHORT_FRONTEND_DATE_FORMAT_DATE_FNS))
    .join(' - ')

export const convertUtcToTimezone = (dateStr, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const hasTimezoneOrZ = /([+-]\d{2}:\d{2}|Z)$/.test(dateStr)
  const dateWithTimezone = hasTimezoneOrZ ? dateStr : `${dateStr}Z`
  const utcDate = new Date(dateWithTimezone)
  const zonedDate = utcToZonedTime(utcDate, timeZone)
  return format(zonedDate, 'yyyy-MM-dd HH:mm')
}

export const convertToUtc = (dateStr, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDate = new Date(dateStr)
  const fixedTimezone = timeZone || defaultTimeZone
  const utcDate = zonedTimeToUtc(zonedDate, fixedTimezone)
  const utcZonedDate = new Date(utcDate.toISOString().replace('Z', ''))
  return format(utcZonedDate, 'yyyy-MM-dd HH:mm')
}

export const convertToUtcKeepTime = (dateStr, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const zonedDate = new Date(dateStr)
  const utcDate = zonedTimeToUtc(zonedDate, timeZone)
  const updatedUtcDate = setMinutes(setHours(utcDate, zonedDate.getHours()), zonedDate.getMinutes())
  return format(updatedUtcDate, 'yyyy-MM-dd HH:mm')
}
